<template>
    <tr class="domain-table-row" @click="goToReports">
        <!-- DOMAIN -->
        <td class="domain-table-body-td">
            <span class="body-td-paragraph domain-name">{{
                capitalizeFirstLetter(domain.domain)
            }}</span>
        </td>

        <!-- STATUS -->
        <td class="domain-table-body-td">
            <span v-if="domain.active" class="body-td-active">active</span>
            <span v-else class="body-td-inactive">inactive</span>
        </td>

        <!-- CREATED -->
        <td class="domain-table-body-td">
            <span class="body-td-paragraph">
                {{
                    format(new Date(domain.created_at as string), "d. L. yyyy")
                }}
            </span>
        </td>

        <!-- DMARC RECORD POLICY -->
        <td class="domain-table-body-td">
            <span v-if="domain.dmarcRecord">
                <span
                    v-if="domain.dmarcPolicy === 'reject'"
                    class="body-td-reject"
                    >reject</span
                >
                <span
                    v-else-if="domain.dmarcPolicy === 'quarantine'"
                    class="body-td-quarantine"
                    >quarantine</span
                >
                <span
                    v-else-if="domain.dmarcPolicy === 'none'"
                    class="body-td-none"
                    >none</span
                >
                <span v-else class="body-td-none">unknown</span>
            </span>

            <!--            <span v-else-if="domain.active && domain.dmarcRecord && domain.report_30 === 0" class="flex gap-1 text-gray-500">-->
            <!--                <span class="body-td-waiting">-->
            <!--                    <span class="text-sm">{{-->
            <!--                        t("vue.waiting_for_reports")-->
            <!--                    }}</span>-->
            <!--                </span>-->
            <!--            </span>-->

            <span v-else class="body-td-none">none</span>
        </td>

        <!-- EMAILS 30D -->
        <!--        <td class="domain-table-body-td">-->
        <!--            <span class="body-td-paragraph">-->
        <!--                {{ domain.report_30?.sum ?? 0 }}-->
        <!--            </span>-->
        <!--        </td>-->

        <!-- KEBAB MENU -->
        <td class="domain-table-body-td actions-cell" @click.stop>
            <button
                ref="kebabBtn"
                class="body-td-misc-button"
                aria-haspopup="menu"
                :aria-expanded="menuOpen ? 'true' : 'false'"
                aria-label="Open actions"
                @click="toggleMenu"
            />
            <transition name="fade">
                <div
                    v-if="menuOpen"
                    ref="menu"
                    class="menu-popover"
                    role="menu"
                    @keydown.esc.stop.prevent="closeMenu"
                >
                    <button
                        class="menu-item"
                        role="menuitem"
                        @click="goToSettings"
                    >
                        Settings
                    </button>
                    <button
                        class="menu-item"
                        role="menuitem"
                        @click="goToReports"
                    >
                        See reports
                    </button>
                    <button
                        class="menu-item"
                        role="menuitem"
                        @click="onRefresh"
                    >
                        Refresh
                    </button>
                    <button
                        class="menu-item danger"
                        role="menuitem"
                        @click="onDelete"
                    >
                        Delete domain
                    </button>
                </div>
            </transition>
        </td>
    </tr>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import { onMounted, onBeforeUnmount, ref, computed } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { format } from "date-fns";
import type IDomain from "@/types/Domains/IDomain";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";
import { capitalizeFirstLetter } from "@/utils/stringUtils";

const { t } = useTranslations();

const emit = defineEmits(["remove", "validate", "refreshDomains"]);

const props = defineProps({
    domain: { type: Object as PropType<IDomain>, required: true },
    validationLoading: { type: Boolean, default: false },
    dnsChecking: { type: Boolean, default: false },
});

const page = usePage<IRhizomPageProps>();
const globalProps = computed(() => page.props);

const menuOpen = ref(false);
const kebabBtn = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const goToReports = () => {
    router.get(route("reports.show", { domain: props.domain.domain }));
};

const goToSettings = () => {
    const url =
        route("reports.show", { domain: props.domain.domain }) + "/settings";
    router.get(url);
};

const onRefresh = () => {
    closeMenu();
    emit("refreshDomains");
};

const onDelete = () => {
    closeMenu();
    emit("remove", props.domain.domain);
};

const toggleMenu = () => {
    menuOpen.value = !menuOpen.value;
    if (menuOpen.value) {
        requestAnimationFrame(() => {
            const rect = menu.value?.getBoundingClientRect();
            if (rect && rect.right > window.innerWidth - 8) {
                menu.value!.style.right = "auto";
                menu.value!.style.left = "0";
            } else {
                menu.value!.style.left = "auto";
                menu.value!.style.right = "0";
            }
        });
    }
};

const closeMenu = () => {
    menuOpen.value = false;
};

const onClickOutside = (e: MouseEvent) => {
    if (!menuOpen.value) return;
    const target = e.target as Node;
    if (
        menu.value &&
        !menu.value.contains(target) &&
        kebabBtn.value &&
        !kebabBtn.value.contains(target)
    ) {
        closeMenu();
    }
};

onMounted(() => {
    document.addEventListener("click", onClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onClickOutside);
});
</script>

<style>
.menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
}
.menu-item:hover {
    background: #f9fafb;
}
.menu-item.danger {
    color: #b91c1c;
}
.menu-item.danger:hover {
    background: #fef2f2;
}

.domain-table-row {
    align-items: center;
    border-bottom: 1px solid #e5e5e5;
}

.domain-table-body-td {
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    height: 72px;
    border-bottom: 1px solid #e5e5e5;
    background: #fff;
    min-width: 0;
}

.body-td-paragraph {
    flex: 1 1 auto;
    min-width: 0;
    color: #1f2938;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.body-td-active,
.body-td-inactive {
    display: inline-flex;
    padding: 4px 8px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}
.body-td-active {
    border: 1px solid #cdebd9;
    background: #effdf4;
    color: #0a7d3a;
}
.body-td-inactive {
    border: 1px solid #d2d6dc;
    background: #f3f5f8;
    color: #6b7280;
}

.body-td-reject,
.body-td-quarantine,
.body-td-none {
    display: inline-flex;
    padding: 4px 8px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    text-transform: lowercase;
}

.body-td-reject {
    border: 1px solid #cdebd9;
    background: #effdf4;
    color: #0a7d3a;
}

.body-td-quarantine {
    border: 1px solid #4bbcff;
    background: #f6faff;
    color: #23aaff;
}

.body-td-none {
    border: 1px solid #d2d6dc;
    background: #f3f5f8;
    color: #707070;
}

.body-td-misc-button {
    display: inline-flex;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    border: 1px solid #d2d6dc;
    background: #f3f5f8;
    cursor: pointer;
    line-height: 1;
}
.body-td-misc-button::before {
    content: "⋮";
    font-size: 16px;
}

.domain-table-row {
    transition: background-color 0.12s ease;
}

.domain-table-row:last-child .domain-table-body-td {
    border-bottom: 0;
}

.domain-table-row > :nth-child(6) {
    justify-content: flex-end;
}

.domain-table-row:hover .domain-table-body-td {
    background: #fafafa;
}

.domain-table-body-td:nth-child(6) {
    text-align: right;
}

.domain-table-row > :nth-child(6) {
    text-align: right;
}

@media (max-width: 900px) {
    .domain-table-row {
        grid-template-columns:
            minmax(160px, 2fr) 120px 120px 140px minmax(100px, 1fr)
            44px;
    }
}

.actions-cell {
    position: relative;
    text-align: right;
}

.menu-popover {
    position: absolute;
    right: 0;
    top: 40px;
    z-index: 9999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}
.menu-popover:hover,
.menu-item:hover {
    cursor: pointer;
}

.domain-name {
    color: #2b7fff;
}

.domain-table-body-td:hover {
    cursor: pointer;
}
</style>
